import client from "services/ngage/api-client";
import {Attribute, AttributeData, AttributeType} from "types/Attribute";
import deserializeAttribute from "./deserializers/attribute";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

// Types ----

type AttributeFilters = {
    search?: string | null
    uuids?: string[] | string | null
    ids?: number[] | number | null
    slugs?: string[] | string | null
    types?: AttributeType[] | string[] | null
    sort?: string[] | string | null
    page?: number
    perPage?: number
}

// Requests ----

/**
 * Get all attributes that match the given filters (paginated)
 */
async function filtered(filters: AttributeFilters = {}) {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<Attribute> | null>('/attributes', {params})).data

        if (page) page.data = page.data.map(deserializeAttribute)

        return page
    } catch (error) {
        console.error("Unable to fetch attributes", error)
        return null
    }
}

/**
 * Get a single attribute
 *
 * @param {number} attributeId
 */
async function get(attributeId: number) {
    try {
        const attribute = (await client.get<Attribute | null>(`/attributes/${attributeId}`)).data
        return attribute ? deserializeAttribute(attribute) : null
    } catch (error) {
        console.error(`Unable to fetch attribute ${attributeId}`, error)
        return null
    }
}

/**
 * Store a new attribute
 *
 * @param data
 */
async function store(data: AttributeData) {
    try {
        const attribute = (await client.post<Attribute | null>('/attributes', data)).data
        return attribute ? deserializeAttribute(attribute) : null
    } catch (error) {
        console.error("Unable to store attribute", error)
        throw error
    }
}

/**
 * Update an existing attribute
 *
 * @param {number} attributeId
 * @param data
 */
async function update(attributeId: number, data: AttributeData) {
    try {
        const attribute = (await client.put<Attribute | null>(`/attributes/${attributeId}`, data)).data
        return attribute ? deserializeAttribute(attribute) : null
    } catch (error) {
        console.error(`Unable to update attribute ${attributeId}`, error)
        throw error
    }
}

/**
 * Permanently delete a attribute
 *
 * @param {number} attributeId
 */
async function destroy(attributeId: number) {
    try {
        return (await client.delete<null>(`/attributes/${attributeId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete attribute ${attributeId}`, error)
        throw error
    }
}

const attributes = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default attributes;